import React, { CSSProperties, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  AllCommunityModule,
  FilterChangedEvent,
  GetRowIdParams,
  GridApi,
  GridReadyEvent,
  ICellRendererParams,
  ModuleRegistry,
  RowClassParams,
  RowClickedEvent,
  RowStyle,
  ValueFormatterParams,
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import CustomTooltip from './gridComponents/CustomTooltip';
import { AGGridProps } from './agProps';
import { Box, Tooltip } from '@mui/material';
import { LoadingSpinner } from 'components/loadingSpinner/LoadingSpinner';
import { themeZafire } from '@zafire/ag-grid-enterprise';
import { AllEnterpriseModule, SetFilterModule } from 'ag-grid-enterprise';
import { humanReadableString } from '../../utils/stringUtils';

ModuleRegistry.registerModules([AllCommunityModule, SetFilterModule, AllEnterpriseModule]);

const truncatedCellStyle: CSSProperties = {
  display: 'block',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const TruncatedCellRenderer = (params: ICellRendererParams) => {
  const spanRef = useRef<HTMLSpanElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useLayoutEffect(() => {
    const el = spanRef.current;
    if (el) {
      setIsOverflowing(el.scrollWidth > el.clientWidth);
    }
  });

  if (!params?.value) return null;

  const displayValue =
    params.valueFormatted !== undefined && params.valueFormatted !== null ? params.valueFormatted : params.value;
  const cellValue = String(displayValue);

  return (
    <Tooltip title={cellValue} arrow disableHoverListener={!isOverflowing}>
      <span ref={spanRef} style={{ ...truncatedCellStyle, cursor: isOverflowing ? 'pointer' : 'default' }}>
        {cellValue}
      </span>
    </Tooltip>
  );
};

const AGGridComponent = (props: AGGridProps) => {
  const gridRef = useRef<AgGridReact>(null);
  const [gridApi, setGridApi] = React.useState<GridApi | undefined>();
  const [filters, setFilters] = useState<Record<string, unknown>>({}); // filters state
  const [paginationPageSize, setPaginationPageSize] = useState<number>(props.defaultRowsPerPage ?? 10);

  const loadingOverlayComponent = useMemo(() => {
    return LoadingSpinner;
  }, []);

  const containerStyle: CSSProperties = useMemo(
    () => ({
      width: '100%',
      height: '100%',
      minHeight: 'inherit',
      display: 'flex',
      flexDirection: 'column',
    }),
    [],
  );

  const gridStyle: CSSProperties = useMemo(
    () => ({
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    }),
    [],
  );

  const onColumnResized = useCallback(() => {
    // Check if the grid API is available
    if (gridApi) {
      // Iterate through each row node and refresh the cell renderer to update tooltips
      gridApi.forEachNode((node) => {
        gridApi.refreshCells({
          rowNodes: [node],
          force: true,
          suppressFlash: true,
        });
      });
    }
  }, [gridApi]);

  const DefaultColumnDef = {
    enableCellChangeFlash: false,
    sortable: true,
    flex: 1,
    resizable: true,
    filter: 'agSetColumnFilter',
    filterParams: {
      buttons: ['reset', 'apply'] as const,
      closeOnApply: true,
      valueFormatter: (params: ValueFormatterParams) => humanReadableString(params.value),
    },
    cellRenderer: TruncatedCellRenderer,
    tooltipComponent: props.customTooltip ?? CustomTooltip,
    headerComponentParams: {
      filters: filters,
      cancelFilterIcon: props.cancelFilterIcon ?? 'fa-times',
      showHeaderFilterIcon: props.showHeaderFilterIcon ?? true,
      openFilterMenuOnHeaderClick: props.openFilterMenuOnHeaderClick ?? true,
    },
  };

  // set background colour on even rows again, this looks bad, should be using CSS classes
  const getRowStyle = useCallback(
    (params: RowClassParams<Record<string, unknown>>): RowStyle | undefined => {
      if (params?.data?.taskUid) {
        return {
          background: params.data.taskUid === props.selectedItem?.taskUid ? '#EFEEE9' : 'white',
        };
      }

      if (params?.data?.serviceAgreementId) {
        return {
          background: params.data.serviceAgreementId === props.selectedItem?.serviceAgreementId ? '#EFEEE9' : 'white',
          cursor: 'pointer',
        };
      }

      if (params?.data?.techId) {
        return {
          background: params.data.techId === props.selectedItem?.techId ? '#EFEEE9' : 'white',
        };
      }

      return { background: 'white' };
    },
    [props],
  );

  /**
   * Sets row id to each row in the ag grid
   * @param params
   */
  const getRowId = useCallback(
    (params: GetRowIdParams) => {
      return params.data[props.rowKey];
    },
    [props],
  );

  const onGridReady = useCallback(
    (params: GridReadyEvent) => {
      setGridApi(params.api);
      setFilters(params.api.getFilterModel());
      if (props.setGridApi) {
        props.setGridApi(params.api);
      }

      return () => {
        setGridApi(undefined);
      };
    },
    [props],
  );

  const onPaginationChanged = useCallback(() => {
    if (gridApi) {
      const currentPageSize = gridApi.paginationGetPageSize();
      if (currentPageSize !== paginationPageSize) {
        setPaginationPageSize(currentPageSize);
      }
    }
  }, [gridApi, paginationPageSize]);

  const onFilterChanged = useCallback((event: FilterChangedEvent) => {
    const existingFilters = event.api.getFilterModel();
    setFilters(existingFilters);
  }, []);

  const onFirstDataRendered = useCallback(() => {
    if (gridApi && !props.disableSizeColumnsToFit) {
      gridApi.sizeColumnsToFit();
    }

    return () => {
      if (gridApi) {
        gridApi.destroy();
        setGridApi(undefined);
      }
    };
  }, [gridApi, props.disableSizeColumnsToFit]);

  const handleRowClick = useCallback(
    (event: RowClickedEvent) => {
      if (props.onRowClick) {
        props.onRowClick(event);
      }
    },
    [props],
  );

  useEffect(() => {
    if (props?.loading) {
      gridApi?.showLoadingOverlay();
    } else {
      gridApi?.hideOverlay();
    }
  }, [props?.loading, gridApi]);

  useEffect(() => {
    if (gridRef?.current?.api) {
      gridRef?.current?.api.redrawRows();
    }
  }, [props.selectedItem]);

  return (
    <div style={props.containerStyle ?? containerStyle} data-testid={'AgGrid-container'}>
      <Box
        style={props?.gridStyle ?? gridStyle}
        sx={{
          height: '100%',
          width: '100%',
          boxSizing: 'border-box', // if we don't have that, we can't put AgGrid into Zf-components because their shadow dom does something nasty and headers of the tables end up clipping
          // Override the AG Grid internal min-height
          '& .ag-center-cols-viewport, & .ag-center-cols-container': {
            minHeight: '40px !important',
          },
        }}
      >
        <AgGridReact
          ref={gridRef}
          animateRows={true}
          domLayout={props.domLayout ?? 'autoHeight'}
          popupParent={props.defaultPopupParent ? null : document.body}
          ensureDomOrder={true}
          cellFlashDuration={1}
          onColumnResized={onColumnResized}
          tooltipShowDelay={150}
          getRowId={getRowId}
          getRowStyle={getRowStyle}
          loadingOverlayComponent={loadingOverlayComponent}
          onGridReady={onGridReady}
          onRowClicked={props.disableSelection ? undefined : handleRowClick}
          onFirstDataRendered={onFirstDataRendered}
          detailCellRendererParams={props.detailCellRendererParams ?? undefined}
          detailRowAutoHeight={props.detailRowAutoHeight ?? undefined}
          masterDetail={props.masterDetail ?? undefined}
          isRowMaster={props.isRowMaster ?? undefined}
          rowSelection={
            props.disableSelection
              ? undefined
              : {
                  mode: 'singleRow',
                  enableClickSelection: true,
                  checkboxes: false,
                  ...props.rowSelection,
                }
          }
          onFilterChanged={props.onFilterChanged ?? onFilterChanged}
          suppressColumnVirtualisation={props.disableColumnVirtualisation}
          suppressMenuHide={props?.showHeaderFilterIcon ?? true}
          columnDefs={props?.getColumnDefs}
          rowData={props.items}
          rowHeight={props.rowHeight ?? 44}
          pagination={props?.showPagination ?? props.items.length > 10}
          paginationAutoPageSize={false}
          suppressRowHoverHighlight={props?.suppressRowHoverHighlight ?? true}
          suppressClickEdit={props?.suppressClickEdit ?? true}
          singleClickEdit={true}
          suppressFocusAfterRefresh={true}
          suppressCellFocus={true}
          paginationPageSizeSelector={[10, 20, 50]}
          theme={themeZafire}
          stopEditingWhenCellsLoseFocus={true}
          suppressPaginationPanel={false}
          paginationPageSize={paginationPageSize}
          onPaginationChanged={onPaginationChanged}
          defaultColDef={DefaultColumnDef}
          overlayLoadingTemplate={
            props?.loadingMessage ?? '<span class="ag-overlay-loading-center">Please wait, loading...</span>'
          }
          overlayNoRowsTemplate={props?.noRowsMessage ?? '<span style="padding: 10px;">No data.</span>'}
          treeData={props?.treeData ?? false}
          autoGroupColumnDef={props.autoGroupColumnDef}
          getDataPath={props.getDataPath}
          groupDisplayType={props.groupDisplayType}
          groupDefaultExpanded={props.groupDefaultExpanded}
        ></AgGridReact>
      </Box>
    </div>
  );
};

export default AGGridComponent;
